import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import { CustomerPoco, CustomerVehiclePoco, PaymentPoco, QuotationDto, QuotationInsuranceProviderAddOnsPoco,
  QuotationInsuranceProviderPoco, QuotationPoco, QuotationAttachmentPoco, ResponseDTO, ValueReplacementDTO, RoadTaxRequestDto
} from '@data/DTO/DTO';

const GET_QUOTATION_BY_ID = gql`
  query quotation($id: Long!) {
    quotation {
      quotationByID(id: $id) {
        success
        error
        data {
            id
            insuranceType
            roadtaxPrice
            roadtaxDigitalCharge
            roadtaxDeliveryCharge
            serviceCharge
            llop
            remarks
            refStatusId
            customer {
              id
              name 
              nric
              phoneNumber
              email
              firstAddress
              secondAddress
              city
              state
              postcode
              country
            }
            picUser {
              id
              firstName
              lastName
            }
            customerVehicle {
              id
              brand
              model
              plateNumber
              currentInsuranceExpirationDate
              engineCapacity
              evpExpirationDate
              isEhailing
              manufactureYear
              ncd
              psvExpirationDate
              puspakomExpirationDate
              vehicleType
            } 
            quotationInsuranceProviderList {
              id
              basicPremium
              isSelected
              insuranceProvider {
                id
                code
                email
                formula
                name
                phoneNumber
                insuranceType
                insuranceProviderBenefitList {
                  benefit
                  id
                }
              }
              quotationAddOnsPoco {
                id
                price
                isSelected
                refQuotationAddOnsType {
                  id
                  name
                }
              }
            }
        }
      }
    }
  }
`;

const GET_LIST_QUOTATION = gql`
  query quotation($pageNo: Int!, $comparison: [ComparisonPocoInput!]) {
    quotation {
        listQuotation(pageNo: $pageNo, comparison: $comparison) {
          success
          error
          totalRecord
          data {
              id
              guid
              isActive
              createdDate
              insuranceType
              trackingNumber
              customer {
                name
                nric
                phoneNumber
                email
                firstAddress
                secondAddress
                city
                state
                postcode
                country
              }
              customerVehicle {
                brand
                model
                plateNumber
                currentInsuranceExpirationDate
                engineCapacity
                evpExpirationDate
                isEhailing
                manufactureYear
                ncd
                psvExpirationDate
                puspakomExpirationDate
                vehicleType
              }
              picUser {
                id
                firstName
                lastName
              } 
              quotationInsuranceProviderList {
                isSelected
                insuranceProvider {
                  insuranceType
                }
              }
              refStatus {
                id
                name
              }
          }
        }
    }
  }
`;

const CREATE_UPDATE_QUOTATION = gql`
    mutation class($input: QuotationPocoInput!) {
      class {
        addUpdateQuotation(input: $input) {
          success
          error
        }
      }
    }
`;

const DELETE_QUOTATION = gql`
  mutation class($quotationId: Long!) {
    class {
      deleteQuotation(quotationId: $quotationId) {
        success
        error
      }
    }
  }
`;

const DELETE_QUOTATION_PROVIDER = gql`
  mutation quotationInsuranceProvider($quotationInsuranceProviderId: Long!) {
    quotationInsuranceProvider {
      deleteQuotationInsuranceProvider(quotationInsuranceProviderId: $quotationInsuranceProviderId) {
        success
        error
      }
    }
  }
`;

const DELETE_QUOTATION_ADDONS = gql`
  mutation quotationInsuranceProviderAddOns($quotationInsuranceProviderAddOnsId: Long!) {
    quotationInsuranceProviderAddOns {
      deleteQuotationInsuranceProviderAddOns(quotationInsuranceProviderAddOnsId: $quotationInsuranceProviderAddOnsId) {
        success
        error
      }
    }
  }
`;

const GET_QUOTATION_BY_GUID = gql`
  query quotation($guid: UUID!) {
    quotation {
      quotationByGuid(guid: $guid) {
        success
        error
        data {
            id
            ehailingCharge
            insuranceType
            roadtaxPrice
            isRoadtax
            roadtaxDigitalCharge
            roadtaxDeliveryCharge
            isPhysicalRoadtax
            serviceCharge
            llop
            remarks
            customerId
            customerVehicleId
            guid
            trackingNumber
            refStatus {
              id
            }
            picUser {
              id
            }
            customer {
              nric
              id
              name
              phoneNumber
              email
              firstAddress
              secondAddress
              city
              state
              postcode
              country
            }
            customerVehicle {
              id
              brand
              model
              plateNumber
              currentInsuranceExpirationDate
              engineCapacity
              evpExpirationDate
              isEhailing
              manufactureYear
              ncd
              psvExpirationDate
              puspakomExpirationDate
              vehicleType
            } 
            quotationInsuranceProviderList {
              id
              basicPremium
              isSelected
              insuranceProvider {
                id
                code
                email
                formula
                name
                phoneNumber
                insuranceType
                insuranceProviderBenefitList {
                  benefit
                  id
                }
              }
              quotationAddOnsPoco {
                id
                price
                isSelected
                refQuotationAddOnsType {
                  id
                  insuranceProviderAddOnsId
                  name
                }
            }
            }
        }
      }
    }
  }
`;

const GET_LIST_PAYMENT = gql`
  query payment($pageNo: Int!) {
    payment {
        listUser(pageNo: $pageNo) {
          success
          error
          totalRecord
          data {
              id
              isActive
              createdDate
              totalPaid
              transactionId
              paymentMethod
              refStatus {
                id
                name
              }
              quotation {
                id
                guid
                insuranceType
                roadtaxPrice
                roadtaxDigitalCharge
                roadtaxDeliveryCharge
                serviceCharge
                llop
                remarks
                isRoadtax
                isPhysicalRoadtax
                trackingNumber
                customer {
                  id
                  name
                  nric
                  phoneNumber
                  email
                  firstAddress
                  secondAddress
                  city
                  state
                  postcode
                  country
                }
                customerVehicle {
                  id
                  brand
                  model
                  plateNumber
                  currentInsuranceExpirationDate
                  engineCapacity
                  evpExpirationDate
                  isEhailing
                  manufactureYear
                  ncd
                  psvExpirationDate
                  puspakomExpirationDate
                  vehicleType
                }
                picUser {
                  id
                  firstName
                  lastName
                }
                quotationInsuranceProviderList {
                  id
                  basicPremium
                  isSelected
                  insuranceProvider {
                    id
                    code
                    email
                    formula
                    name
                    phoneNumber
                    insuranceType
                    insuranceProviderBenefitList {
                      benefit
                      id
                    }
                  }
                  quotationAddOnsPoco {
                    id
                    price
                    isSelected
                    refQuotationAddOnsType {
                      id
                      name
                    }
                  }
                }
              }
          }
        }
    }
  }
`;

const GET_PAYMENT_BY_ID = gql`
  query payment($id: Long!) {
    payment {
      userByID(id: $id) {
        success
        error
        data {
            id
            totalPaid
            transactionId
            paymentMethod
            refStatus {
              id
              name
            }
            quotation {
              id
              insuranceType
              roadtaxPrice
              roadtaxDigitalCharge
              roadtaxDeliveryCharge
              serviceCharge
              isRoadtax
              isPhysicalRoadtax
              llop
              remarks
              customer {
                id
                name
                nric
                phoneNumber
                email
                firstAddress
                secondAddress
                city
                state
                postcode
                country
              }
              picUser {
                id
                firstName
                lastName
              }
              customerVehicle {
                id
                brand
                model
                plateNumber
                currentInsuranceExpirationDate
                engineCapacity
                evpExpirationDate
                isEhailing
                manufactureYear
                ncd
                psvExpirationDate
                puspakomExpirationDate
                vehicleType
              } 
              quotationInsuranceProviderList {
                id
                basicPremium
                isSelected
                insuranceProvider {
                  id
                  code
                  email
                  formula
                  name
                  phoneNumber
                  insuranceType
                  insuranceProviderBenefitList {
                    benefit
                    id
                  }
                }
                quotationAddOnsPoco {
                  id
                  price
                  isSelected
                  refQuotationAddOnsType {
                    id
                    name
                  }
                }
              }
            }
        }
      }
    }
  }
`;

const CREATE_PAYMENT = gql`
    mutation payment($input: PaymentPocoInput!, $quotation: QuotationPocoInput!, $customer: CustomerPocoInput!, $customerVehicle: CustomerVehiclePocoInput!,) {
      payment {
        createPayment(input: $input, quotation: $quotation, customer: $customer, customerVehicle: $customerVehicle) {
          success
          error
          data {
            url
          }
        }
      }
    }
`;

const REQUEST_QUOTATION = gql`
    mutation class($input: QuotationDtoInput!, $roadtax: RoadTaxRequestDtoInput!) {
      class {
        requestQuotation(input: $input, roadtax: $roadtax) {
          success
          error
        }
      }
    }
`;

const GET_QUOTATION_ATTACHMENT_BY_QUOTATIONID = gql`
  query quotationAttachment($quotationId: Long!) {
    quotationAttachment {
      quotationAttachmentByQuotationId(quotationId: $quotationId) {
        success
        error
        data {
            id
            attachmentFor
            attachmentName
            attachmentPath
            attachmentType
            quotationGuid
        }
      }
    }
  }
`;

const CREATE_QUOTATION_ATTACHMENT = gql`
  mutation quotationAttachment($input: QuotationAttachmentPocoInput!) {
    quotationAttachment {
      addUpdateQuotationAttachment(input: $input) {
        success
        error
      }
    }
  }
`;

const SEND_WHATSAPP = gql`
  query whatsApp($whatsappTemplateId: Long!, $refStatusId: Long!, $valueReplacement: ValueReplacementDTOInput!) {
    whatsApp {
      sendWhatsapp(whatsappTemplateId: $whatsappTemplateId, refStatusId: $refStatusId, valueReplacement: $valueReplacement) {
        success
        error
      }
    }
  }
`;

@Injectable({
  providedIn: 'root',
})
export class ApolloQuotationService {
  constructor(private apollo: Apollo) { }

  getQuotationById(id: number): Observable<ResponseDTO<QuotationPoco[]>> {
    return this.apollo.query({
      query: GET_QUOTATION_BY_ID,
      variables: { id },
    }).pipe(
      map((result: any) => result.data?.quotation?.quotationByID)
    );
  }

  getPaymentById(id: number): Observable<ResponseDTO<PaymentPoco[]>> {
    return this.apollo.query({
      query: GET_PAYMENT_BY_ID,
      variables: { id },
    }).pipe(
      map((result: any) => result.data?.payment?.userByID)
    );
  }

  getListQuotation(pageNo: number = 1, comparison: { columnName: string; condition: string; value: string }[] = []): Observable<ResponseDTO<QuotationPoco[]>> {
    return this.apollo.query({
      query: GET_LIST_QUOTATION,
      variables: { pageNo, comparison },
    }).pipe(
      map((result: any) => result.data?.quotation?.listQuotation)
    );
  }

  getListPayment(pageNo: number = 1): Observable<ResponseDTO<PaymentPoco[]>> {
    return this.apollo.query({
      query: GET_LIST_PAYMENT,
      variables: { pageNo },
    }).pipe(
      map((result: any) => result.data?.payment?.listUser)
    );
  }

  createUpdateQuotation(quotation: Partial<QuotationPoco>): Observable<ResponseDTO<QuotationPoco[]>> {
    const cleanQuotation = this.removeTypename(quotation);
    return this.apollo.mutate({
      mutation: CREATE_UPDATE_QUOTATION,
      variables: { input: cleanQuotation },
    }).pipe(
      map((result: any) => result.data?.class?.addUpdateQuotation)
    );
  }

  requestQuotation(quotation: Partial<QuotationDto>, roadtax: Partial<RoadTaxRequestDto>): Observable<ResponseDTO<QuotationDto[]>> {
    return this.apollo.mutate({
      mutation: REQUEST_QUOTATION,
      variables: { input: quotation, roadtax },
    }).pipe(
      map((result: any) => result.data?.class?.requestQuotation)
    );
  }

  deleteQuotation(quotationId: number): Observable<ResponseDTO<QuotationPoco>> {
    return this.apollo.mutate({
      mutation: DELETE_QUOTATION,
      variables: { quotationId },
    }).pipe(
      map((result: any) => result.data?.class?.deleteQuotation)
    );
  }

  deleteQuotationProvider(quotationInsuranceProviderId: number): Observable<ResponseDTO<QuotationInsuranceProviderPoco>> {
    return this.apollo.mutate({
      mutation: DELETE_QUOTATION_PROVIDER,
      variables: { quotationInsuranceProviderId },
    }).pipe(
      map((result: any) => result.data?.quotationInsuranceProvider?.deleteQuotationInsuranceProvider)
    );
  }

  deleteQuotationAddOns(quotationInsuranceProviderAddOnsId: number): Observable<ResponseDTO<QuotationInsuranceProviderAddOnsPoco>> {
    return this.apollo.mutate({
      mutation: DELETE_QUOTATION_ADDONS,
      variables: { quotationInsuranceProviderAddOnsId },
    }).pipe(
      map((result: any) => result.data?.quotationInsuranceProviderAddOns?.deleteQuotationInsuranceProviderAddOns)
    );
  }

  getQuotationByGUID(guid: string): Observable<ResponseDTO<QuotationPoco[]>> {
    return this.apollo.query({
      query: GET_QUOTATION_BY_GUID,
      variables: { guid },
    }).pipe(
      map((result: any) => result.data?.quotation?.quotationByGuid)
    );
  }

  createPayment(payment: Partial<PaymentPoco>, quotation: Partial<QuotationPoco>, customer: Partial<CustomerPoco>, customerVehicle: Partial<CustomerVehiclePoco>,): Observable<ResponseDTO<PaymentPoco>> {
    return this.apollo.mutate({
      mutation: CREATE_PAYMENT,
      variables: { input: payment, quotation, customer, customerVehicle },
    }).pipe(
      map((result: any) => result.data?.payment?.createPayment)
    );
  }

  getQuotationAttachmentByQuotationId(quotationId: number): Observable<ResponseDTO<QuotationAttachmentPoco[]>> {
    return this.apollo.query({
      query: GET_QUOTATION_ATTACHMENT_BY_QUOTATIONID,
      variables: { quotationId },
    }).pipe(
      map((result: any) => result.data?.quotationAttachment?.quotationAttachmentByQuotationId)
    );
  }

  createQuotationAttachment(quotation: Partial<QuotationAttachmentPoco>): Observable<ResponseDTO<QuotationAttachmentPoco[]>> {
    return this.apollo.mutate({
      mutation: CREATE_QUOTATION_ATTACHMENT,
      variables: { input: quotation },
    }).pipe(
      map((result: any) => result.data?.quotationAttachment?.addUpdateQuotationAttachment)
    );
  }

  sendWhatsapp(whatsappTemplateId: number, refStatusId: number, valueReplacement: Partial<ValueReplacementDTO>): Observable<ResponseDTO<ValueReplacementDTO[]>> {
    return this.apollo.query({
      query: SEND_WHATSAPP,
      variables: { whatsappTemplateId, refStatusId, valueReplacement: valueReplacement },
    }).pipe(
      map((result: any) => result.data?.whatsApp?.sendWhatsapp)
    );
  }

  private removeTypename(data: any): any {
    if (Array.isArray(data)) {
      return data.map(item => this.removeTypename(item));
    } else if (data !== null && typeof data === 'object') {
      const newObj: any = {};
      Object.keys(data).forEach(key => {
        if (key !== '__typename') {
          newObj[key] = this.removeTypename(data[key]);
        }
      });
      return newObj;
    }
    return data;
  }
}